"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import Link from "next/link";

import cn from "classnames";

import { brown } from "@/app/font";
import type { NavigationGroup, NavigationItem } from "@/sanity.types";

import { HeaderProps } from "./header.component";
import { HeaderMegaMenuFeaturedItems } from "./headerMegaMenuFeaturedItems.component";

// Short grace period so moving the cursor across the gap between the
// summary and the absolutely-positioned nav doesn't snap the menu shut.
const HOVER_CLOSE_DELAY_MS = 150;

const isShopMegaMenu = (label?: string | null) =>
  label?.trim().toLowerCase() === "shop";

export const HeaderMegaMenu = ({ menu }: HeaderMegaMenuProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [navAlignOffset, setNavAlignOffset] = useState(0);

  // If the menu label is "Shop", we don't want to align the links to the trigger
  const alignLinksToTrigger = !isShopMegaMenu(menu?.label);

  const updateNavAlignment = useCallback(() => {
    if (!alignLinksToTrigger) return;

    const summary = summaryRef.current;
    const nav = navRef.current;
    if (!summary || !nav) return;

    const summaryStyles = getComputedStyle(summary);
    const navStyles = getComputedStyle(nav);

    // Get the left position of the summary content including the padding
    const summaryContentLeft =
      summary.getBoundingClientRect().left +
      Number.parseFloat(summaryStyles.paddingLeft);

    // Get the left position of the nav content including the padding
    const navContentLeft =
      nav.getBoundingClientRect().left +
      Number.parseFloat(navStyles.paddingLeft);

    setNavAlignOffset(Math.max(0, summaryContentLeft - navContentLeft));
  }, [alignLinksToTrigger]);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelScheduledClose();
    detailsRef.current?.removeAttribute("open");
  }, [cancelScheduledClose]);

  const openOnHover = useCallback(
    (event: React.PointerEvent<HTMLLIElement>) => {
      // Only react to fine pointers (mouse/trackpad). Touch should still
      // require a tap on the summary to open the menu.
      if (event.pointerType !== "mouse") return;
      cancelScheduledClose();
      detailsRef.current?.setAttribute("open", "");
      if (alignLinksToTrigger) {
        requestAnimationFrame(updateNavAlignment);
      }
    },
    [alignLinksToTrigger, cancelScheduledClose, updateNavAlignment],
  );

  const scheduleCloseOnHover = useCallback(
    (event: React.PointerEvent<HTMLLIElement>) => {
      if (event.pointerType !== "mouse") return;
      cancelScheduledClose();
      closeTimeoutRef.current = setTimeout(() => {
        detailsRef.current?.removeAttribute("open");
        closeTimeoutRef.current = null;
      }, HOVER_CLOSE_DELAY_MS);
    },
    [cancelScheduledClose],
  );

  useLayoutEffect(() => {
    if (!alignLinksToTrigger) return;

    updateNavAlignment();
    window.addEventListener("resize", updateNavAlignment);
    return () => window.removeEventListener("resize", updateNavAlignment);
  }, [alignLinksToTrigger, updateNavAlignment]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const details = detailsRef.current;
      if (!details?.open) return;
      const target = event.target as Node | null;
      if (!target || details.contains(target)) return;
      details.removeAttribute("open");
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      cancelScheduledClose();
    };
  }, [cancelScheduledClose]);

  const menuLinks = (menu?.items ?? []).flatMap((item) =>
    item._type === "navigationItem" ? [item as NavigationItem] : [],
  );
  const renderNav = () => {
    if (!menuLinks.length) return null;
    return menuLinks.map((item) => {
      if (!item.link.url) return null;
      return (
        <li
          data-testid="header-mega-menu-list-item"
          key={item.label}
          className="flex">
          <Link
            className={cn("w-full p-2 whitespace-nowrap xl:px-4")}
            href={{
              pathname: item.link.url,
              query: item.link.queryString,
            }}>
            {item.label}
          </Link>
        </li>
      );
    });
  };

  // TODO: make this customizable via Sanity for each menu
  const menuLink = menuLinks[0]?.link;

  return (
    <li
      className="list-none"
      onPointerEnter={openOnHover}
      onPointerLeave={scheduleCloseOnHover}>
      <details
        ref={detailsRef}
        name="header-details"
        data-testid="header-mega-menu"
        className="group hidden flex-col lg:flex"
        onToggle={() => {
          if (alignLinksToTrigger) {
            requestAnimationFrame(updateNavAlignment);
          }
        }}>
        <summary
          ref={summaryRef}
          data-testid="header-mega-menu-summary"
          className={cn(
            brown.className,
            "hover:bg-secondary-100 flex w-full cursor-pointer list-none rounded-3xl px-3 py-1.5 font-bold whitespace-nowrap uppercase transition-all duration-600 xl:px-4 xl:text-[18px] [&::-webkit-details-marker]:hidden",
          )}>
          {menuLink ? (
            <Link
              tabIndex={-1}
              href={{
                pathname: menuLink?.url,
              }}>
              {menu?.label}
            </Link>
          ) : (
            menu?.label
          )}
        </summary>
        <nav
          ref={navRef}
          data-testid="header-mega-menu-nav"
          className={cn(
            "border-secondary-100 absolute top-24 right-0 left-0 z-50 flex w-full rounded-[50px] border py-6",
            alignLinksToTrigger ? "pr-10" : "px-10",
          )}
          aria-label={`${menu?.label} menu`}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("a")) close();
          }}
          style={{
            background:
              "linear-gradient(86deg, rgba(0, 184, 241, 0.90) -28.42%, rgba(7, 64, 139, 0.90) 14.14%, var(--Primary-Secondary-Blue-200, rgba(0, 34, 106, 0.90)) 85.26%, var(--Primary-Primary-Blue-300, rgba(0, 184, 241, 0.90)) 137.78%)",
          }}>
          <div
            className="flex items-center xl:flex-1"
            style={
              alignLinksToTrigger ? { paddingLeft: navAlignOffset } : undefined
            }>
            <ul
              data-testid="header-mega-menu-list"
              className="flex flex-col gap-2"
              style={{
                borderLeft: "2px solid transparent",
                borderImage:
                  "linear-gradient(to bottom, var(--color-primary-200), var(--color-secondary))",
                borderImageSlice: 1,
              }}>
              {renderNav()}
            </ul>
          </div>
          <HeaderMegaMenuFeaturedItems featuredItems={menu?.featuredItems} />
        </nav>
      </details>
    </li>
  );
};

export type HeaderMegaMenuProps = HeaderProps & {
  menu?: NavigationGroup;
};
